/**
 * Distinctive share EDMs — pre-designed marketing visuals agents share out,
 * rendered the same way as the price-promo designs (fixed-size markup,
 * rasterised via html2canvas). Each design ends with the standard agent
 * footer:
 *   line 1: {{ agentLine }}  — <agent name> · <CEA> · <mobile>
 *   line 2: {{ agencyLine }} — Huttons Asia Pte Ltd · L3008899K
 *
 * Unlike the square price-promo canvases, each template declares its own
 * width/height (EDMs are portrait).
 */

export type DistinctiveField = 'agentLine' | 'agencyLine';

export interface DistinctiveTemplate {
  id: string;
  name: string;
  /**
   * Template name recorded server-side when this design is shared/downloaded.
   * Defaults to `name`. Lets a design carry a distinct gallery caption while
   * recording a specific share-template (e.g. Super Connector records
   * "Super Connector Register Interest", which unlocks Register Interest).
   */
  shareTemplateName?: string;
  width: number;
  height: number;
  /** Fixed-size styled markup containing {{ field }} placeholders. */
  markup: string;
}

/** Shared bottom bar carrying the agent + agency branding lines. */
function agentFooter(height: number, background: string): string {
  return `<div style="position:absolute;left:0;right:0;bottom:0;height:${height}px;background:${background};border-top:1.5px solid rgba(255,255,255,.14);display:flex;align-items:center;justify-content:center;padding:0 44px;flex-direction:column;gap:3px">
        <span style="font-weight:600;font-size:20px;color:#f2ede1;letter-spacing:.3px">{{ agentLine }}</span>
        <span style="font-weight:500;font-size:16px;color:rgba(242,237,225,.72);letter-spacing:.4px">{{ agencyLine }}</span>
      </div>`;
}

const FOOTER_HEIGHT = 92;

/**
 * A design that is a single full-bleed image with the standard agent footer
 * below it. The image is drawn at `imageWidth x imageHeight` (its intrinsic
 * aspect ratio scaled to 1080px wide) and the footer sits underneath.
 */
function imageTemplate(config: {
  id: string;
  name: string;
  shareTemplateName?: string;
  src: string;
  alt: string;
  imageWidth: number;
  imageHeight: number;
  background: string;
  footerBackground: string;
}): DistinctiveTemplate {
  const totalHeight = config.imageHeight + FOOTER_HEIGHT;
  return {
    id: config.id,
    name: config.name,
    shareTemplateName: config.shareTemplateName,
    width: config.imageWidth,
    height: totalHeight,
    markup: `<div style="position:relative;width:${config.imageWidth}px;height:${totalHeight}px;overflow:hidden;background:${config.background};font-family:'Archivo',sans-serif">
      <img src="${config.src}" alt="${config.alt}" style="position:absolute;top:0;left:0;width:${config.imageWidth}px;height:${config.imageHeight}px;object-fit:cover">
      ${agentFooter(FOOTER_HEIGHT, config.footerBackground)}
    </div>`,
  };
}

/**
 * Newest design first — the gallery renders these in array order, so a newly
 * launched feature's EDM goes at the top of this list.
 */
export const DISTINCTIVE_TEMPLATES: DistinctiveTemplate[] = [
  // Novel Lead Generation (two variants) — intrinsic 1254x1254 (square), scaled
  // to 1080 wide. Skyline records "Novel Lead Generation", Plain records
  // "Novel Lead Generation Plain"; either share unlocks the extra share-link
  // blocks in My Shareable Links (see NOVEL_LEAD_GENERATION_TEMPLATES in the
  // backend distinctiveShareService and /api/share-links).
  imageTemplate({
    id: 'novel-lead-generation-skyline',
    name: 'Novel Lead Generation · Skyline',
    shareTemplateName: 'Novel Lead Generation',
    src: '/distinctive/novel-lead-generation-skyline.jpg',
    alt: 'Novel Lead Generation with Huttons',
    imageWidth: 1080,
    imageHeight: 1080,
    background: '#001847',
    // Sampled from the EDM's bottom edge (deep navy water/glow band).
    footerBackground: '#0a1d45',
  }),
  imageTemplate({
    id: 'novel-lead-generation-halo',
    name: 'Novel Lead Generation · Plain',
    shareTemplateName: 'Novel Lead Generation Plain',
    src: '/distinctive/novel-lead-generation-halo.jpg',
    alt: 'Novel Lead Generation with Huttons',
    imageWidth: 1080,
    imageHeight: 1080,
    background: '#041b43',
    footerBackground: '#041a42',
  }),
  // Super Selling Calculator — intrinsic 1448x1086 (4:3 landscape), scaled to
  // 1080 wide → 810 tall. Sharing this design records the "Super Selling
  // Calculator" share, which unlocks the Selling Calculator in Super Calculator
  // (see userHasSharedTemplate in the backend distinctiveShareService and the
  // /api/super-calculator/user/me handler) — mirroring how the Super Buying
  // Calculator design used to unlock the Buying Calculator.
  imageTemplate({
    id: 'super-selling-calculator',
    name: 'Super Selling Calculator',
    src: '/distinctive/super-selling-calculator-edm.jpeg',
    alt: 'Super Selling Calculator',
    imageWidth: 1080,
    imageHeight: 810,
    // Sampled from the EDM's bottom edge (a flat navy band), so the footer bar
    // continues the artwork instead of reading as a separate strip.
    background: '#021c3b',
    footerBackground: '#021c3b',
  }),
  // Super Ratio — intrinsic 1672x941 (landscape), scaled to 1080 wide → 608
  // tall. Sharing this design records the "Super Ratio" share for analytics.
  imageTemplate({
    id: 'super-ratio',
    name: 'Super Ratio',
    src: '/distinctive/super-ratio1.jpeg',
    alt: 'Super Picker Super Ratio',
    imageWidth: 1080,
    imageHeight: 608,
    background: '#0b1f45',
    // Sampled from the EDM's bottom edge so the footer bar reads as part of the
    // artwork's dark navy base band.
    footerBackground: '#0c2041',
  }),
  // Price Promo Watchlist — intrinsic 1280x960 (4:3), scaled to 1080 wide → 810 tall.
  imageTemplate({
    id: 'price-promo-watchlist',
    name: 'Price Promo Watchlist',
    src: '/distinctive/price-promo-watchlist.jpeg',
    alt: 'Super Picker Price Promo Watchlist',
    imageWidth: 1080,
    imageHeight: 810,
    background: '#050e24',
    footerBackground: '#040a1a',
  }),
  // Super Buying Calculator — intrinsic 1402x1122 (landscape), scaled to 1080
  // wide → 864 tall. Sharing this design records the "Super Buying Calculator"
  // share, which unlocks the Buying Calculator in Super Calculator (see
  // userHasSharedTemplate in the backend distinctiveShareService).
  imageTemplate({
    id: 'super-buying-calculator',
    name: 'Super Buying Calculator',
    src: '/distinctive/buying-calculator-edm-2.jpg',
    alt: 'Super Buying Calculator',
    imageWidth: 1080,
    imageHeight: 864,
    background: '#071a3d',
    footerBackground: '#061229',
  }),
  // Super Connector — intrinsic 1254x1254 (square), scaled to 1080 wide. Sharing
  // this design records the "Super Connector Register Interest" share, which
  // unlocks the Register Interest button in the Distinctives gallery.
  imageTemplate({
    id: 'super-connector',
    name: 'Super Connector',
    shareTemplateName: 'Super Connector Register Interest',
    src: '/distinctive/super-connector.jpeg',
    alt: 'Super Connector',
    imageWidth: 1080,
    imageHeight: 1080,
    background: '#05070d',
    footerBackground: '#04140c',
  }),
  // Rex & Linus Price Promo — intrinsic 1254x1254 (square), scaled to 1080 wide.
  imageTemplate({
    id: 'rex-linus-price-promo',
    name: 'Rex & Linus Price Promo',
    src: '/distinctive/rex-linus-price-promo.png',
    alt: 'Rex & Linus Price Promo',
    imageWidth: 1080,
    imageHeight: 1080,
    background: '#05070d',
    footerBackground: '#04140c',
  }),
  // Price Promo Units — intrinsic 1254x1254 (square), scaled to 1080 wide.
  imageTemplate({
    id: 'price-promo-units',
    name: 'Price Promo Units',
    src: '/distinctive/price-promo-edm.png',
    alt: 'Super Picker Price Promo Units',
    imageWidth: 1080,
    imageHeight: 1080,
    background: '#05070d',
    footerBackground: '#04140c',
  }),
  // Super App Distinctives — intrinsic 693x829, scaled to 1080 wide → 1292 tall.
  imageTemplate({
    id: 'super-app-distinctives',
    name: 'Super App Distinctives',
    src: '/distinctive/distinctive.png',
    alt: 'Huttons Super App Distinctives',
    imageWidth: 1080,
    imageHeight: 1292,
    background: '#0a1f5c',
    footerBackground: '#071022',
  }),
];

const ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** Escape a user-supplied value before injecting it into template markup. */
function escapeHtml(value: string): string {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ESCAPE_MAP[c]);
}

/** Replace {{ field }} placeholders in a template with escaped values. */
export function fillDistinctiveTemplate(
  markup: string,
  values: Record<DistinctiveField, string>,
): string {
  return markup.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key: string) => {
    const v = (values as Record<string, string>)[key];
    return v === undefined ? '' : escapeHtml(v);
  });
}

/** The designs whose share unlocks the extra share-link blocks — the gate prompts one at random. */
export const SHARE_LINK_UNLOCK_TEMPLATES = DISTINCTIVE_TEMPLATES.filter(
  (template) => template.id.startsWith('novel-lead-generation-'),
);
